import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Spinner } from '@/components/ui/spinner';
import i18n from '@/i18n';
import { type ReactNode, useState } from 'react';
import { create } from 'zustand';

interface ConfirmRequest {
  id: number;
  title: ReactNode;
  description?: ReactNode;
  okText: string;
  cancelText: string;
  danger: boolean;
  onOk: () => void | Promise<void>;
  resolve: (confirmed: boolean) => void;
}

interface ConfirmState {
  request: ConfirmRequest | null;
  open: (request: ConfirmRequest) => void;
  close: (confirmed: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  open: (request) => {
    // A second confirm while one is open would orphan the first promise.
    get().request?.resolve(false);
    set({ request });
  },
  close: (confirmed) => {
    const { request } = get();
    request?.resolve(confirmed);
    set({ request: null });
  },
}));

/**
 * Ask before a destructive action, from anywhere — including inside a callback
 * that has no way to render a dialog.
 *
 * The imperative shape is deliberate: call sites live in query mutation
 * callbacks and row handlers where threading a piece of state would mean
 * restructuring the page. The dialog itself is the same AlertDialog used
 * declaratively elsewhere, mounted once by <ConfirmDialogHost />.
 *
 * Resolves `true` when confirmed, `false` when dismissed, so a caller can chain.
 */
export function confirmAction(opts: {
  title: ReactNode;
  description?: ReactNode;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
  onOk: () => void | Promise<void>;
}): Promise<boolean> {
  return new Promise((resolve) => {
    useConfirmStore.getState().open({
      id: Date.now(),
      title: opts.title,
      description: opts.description,
      okText: opts.okText ?? i18n.t('common.confirm', 'Confirm'),
      cancelText: opts.cancelText ?? i18n.t('common.cancel', 'Cancel'),
      danger: opts.danger ?? false,
      onOk: opts.onOk,
      resolve,
    });
  });
}

/**
 * Renders whichever confirmation is outstanding. Mounted once, next to the
 * toast host.
 */
export function ConfirmDialogHost() {
  const request = useConfirmStore((s) => s.request);
  const close = useConfirmStore((s) => s.close);
  const [pending, setPending] = useState(false);

  async function run() {
    if (!request) return;
    setPending(true);
    try {
      await request.onOk();
    } finally {
      setPending(false);
      close(true);
    }
  }

  return (
    <AlertDialog
      open={request !== null}
      onOpenChange={(next) => {
        // Stay open while the action is in flight: closing early would hide the
        // only feedback the user has for a slow request.
        if (!next && !pending) close(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{request?.title}</AlertDialogTitle>
          <AlertDialogDescription className={request?.description ? undefined : 'sr-only'}>
            {request?.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{request?.cancelText}</AlertDialogCancel>
          <AlertDialogAction
            variant={request?.danger ? 'destructive' : 'default'}
            disabled={pending}
            onClick={(event) => {
              // Keep the dialog mounted until onOk settles.
              event.preventDefault();
              void run();
            }}
          >
            {pending && <Spinner data-icon="inline-start" />}
            {request?.okText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
